import { Flex } from 'antd';
import styles from './documents.module.scss';
import { DocumentsTable } from '@/components/documents-table/documents-table';
import { DocumentsHeader } from '@/components/documents-header/documents-header';
import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Outlet, useOutlet } from 'react-router';
import { useDocuments } from '@/hooks/use-documents/use-documents';
import { useEffect, useState } from 'react';
import { DocumentsFilters } from '@/services/documents-service/documents-service.types';
import {
  DEFAULT_PAGINATION,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompanyStore } from '@/store/app.store';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';

export function Documents() {
  const outlet = useOutlet();
  const items = useBreadcrumbs();
  const { companyName, setCompanyName } = useCompanyStore();
  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const { companies } = useCompaniesList();
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;
  const isAdmin = isCompanyAdmin && !!userData?.default_company;

  const [documentsFilters, setDocumentsFilters] = useState<DocumentsFilters>({
    query: undefined,
    type: undefined,
    company_name: companyName || (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
  });

  useEffect(() => {
    if (companyName) {
      setDocumentsFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setDocumentsFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultSorting: { sort_by: 'created_at', order_by: 'desc' },
    });

  const { documentsData, isPending: isPendingDocumentsData } = useDocuments({
    filtersParams: {
      ...documentsFilters,
      company_name: documentsFilters.company_name,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    onPaginationChanged(
      page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  const handleCompanyChange = (filters: DocumentsFilters) => {
    setDocumentsFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />
      {outlet ? (
        <Outlet context={{ companiesList: companies?.map((company) => company.name) || [] }} />
      ) : (
        <div>
          <DocumentsHeader
            onSearchQueryChange={setCompanySearchQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError}
            filters={documentsFilters}
            onChangeFilters={handleCompanyChange}
            documentsNumber={documentsData?.documents_count || 0}
            companiesList={companies?.map((company) => company.name) || []}
            isCompanySelectDisabled={!!isCompanyAdmin && !hasMultipleCompanies}
          />
          <DocumentsTable
            currentPage={paginationDetails.page}
            onChange={handleChange}
            isAdmin={isAdmin}
            pagination={documentsData?.pagination}
            documentsData={documentsData?.documents}
            loading={isPendingDocumentsData}
          />
        </div>
      )}
    </Flex>
  );
}
